import React, { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import useStyles from "./style";
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box
} from "@mui/material";
import { Delete, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment-timezone";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import _ from "lodash";

// Services
import notificationApi from "Services/Notifications";

// Component
import BulkSMSSelector from "./bulksmsmodel";

const Passenger = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const timezone = useSelector((state) => state.timezone.timezone);

  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [row, setRow] = useState([]);
  const [notifyrow, setnotifyRow] = useState([]);
  const [bulkrow, setbulkrow] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  // Get Passenger list api
  const getPassengers = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const params = { page, limit: "unlimited" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    notificationApi.getUsersList(params).then(onSuccess, onFailure);
  }, [demoaccount_id]);

  const getnotifications = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setnotifyRow(res?.data?.data?.data);
      } else {
        setnotifyRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const params = { page, limit: pageSize, notification_type: "SMS" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    notificationApi.getNotificationsList(params).then(onSuccess, onFailure);
  }, [page, pageSize, demoaccount_id]);

  const getallnotifications = useCallback(() => {
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setbulkrow(res?.data?.data?.data);
      } else {
        setbulkrow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const params = { page: 1, limit: "unlimited", notification_type: "SMS" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    notificationApi.getNotificationsList(params).then(onSuccess, onFailure);
  }, [demoaccount_id]);

  useEffect(() => {
    getPassengers();
    getnotifications();
    getallnotifications();
  }, [getPassengers, getnotifications]);

  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkformatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All SMSnotifications.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopy = () => {
    const columnWidths = [5, 25, 15, 15];
    const padText = (text, width) => {
      return (text ?? "").toString().padEnd(width, " ");
    };
    const header = [
      padText("ID", columnWidths[0]),
      padText("Message", columnWidths[1]),
      padText("Passengers", columnWidths[2]),
      padText("Created At", columnWidths[3]),
    ].join("");

    const rows = notifydataRows
      .map((row) =>
        [
          padText(row.formattedId, columnWidths[0]),
          padText(row.message, columnWidths[1]),
          padText(row.passengers, columnWidths[2]),
          padText(row.createdat, columnWidths[3]),
        ].join("")
      )
      .join("\n");

    const formattedContent = [header, rows].join("\n");

    navigator.clipboard.writeText(formattedContent).then(() => {
      dispatch(
        showSnackbar({
          message: "Table copied to clipboard!",
          variant: "success",
          autoHideDuration: 2000,
        })
      );
    });
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatteddatas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notifications");
    XLSX.writeFile(workbook, "SMSnotifications.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "SMSnotifications.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const tableData = notifydataRows.map((row) => [
      row.formattedId,
      row.message,
      row.passengers,
      row.createdat,
    ]);
    doc.autoTable({
      head: [["ID", "Message", "Passengers", "Created At"]],
      body: tableData,
    });
    doc.save("SMSnotifications.pdf");
  };

  function formatDate(inputDate) {
    if (!inputDate) return "--";
    const dateValue = inputDate?.$date || inputDate;
    if (!dateValue) return "--";
    const tz = timezone || "UTC";
    if (!moment(dateValue).isValid()) return "--";
    return moment(dateValue).tz(tz).format("MMM DD, YYYY • HH:mm");
  }

  const calculateSno = (pageIndex, pageSize, index) => {
    return (pageIndex - 1) * pageSize + index + 1;
  };

  const dataRows = _.map(
    _.filter(row || [], (data) => data?.mobileNumber),
    (data) => ({
      email: data?.email,
      mobile: data?.mobileNumber,
      row: data,
    })
  );

  const notifydataRows = _.map(notifyrow || [], (data, index) => {
    const sno = calculateSno(page, pageSize, index);
    const formattedId = `#BR-${9020 + sno}`;
    return {
      id: data?._id,
      sno: sno,
      formattedId: formattedId,
      message: data?.message || "--",
      passengers: Array.isArray(data?.sent_to_names)
        ? data.sent_to_names.join(", ")
        : data?.sent_to_names || "--",
      createdat: formatDate(data?.created_at),
      row: data,
    };
  });

  const formatteddatas = notifydataRows.map((row) => ({
    ID: row.formattedId,
    Message: row.message,
    Passengers: row.passengers,
    "Created At": row.createdat,
  }));

  const bulkdataRows = _.map(
    _.orderBy(bulkrow || [], ["createdAt"], ["asc"]),
    (data, index) => {
      const sno = calculateSno(page, pageSize, index);
      const formattedId = `#BR-${9020 + sno}`;
      return {
        id: data?._id,
        sno: sno,
        formattedId: formattedId,
        message: data?.message || "--",
        passengers: Array.isArray(data?.sent_to_names)
          ? data.sent_to_names.join(", ")
          : data?.sent_to_names || "--",
        createdat: formatDate(data?.created_at),
        row: data,
      };
    }
  );

  const bulkformatteddatas = bulkdataRows.map((row) => ({
    ID: row.formattedId,
    Message: row.message,
    Passengers: row.passengers,
    "Created At": row.createdat,
  }));

  const columns = [
    {
      field: "formattedId",
      headerName: "ID",
      flex: 0.6,
      sortable: false,
      renderCell: (params) => (
        <Typography className={classes.idText}>
          {params?.row?.formattedId}
        </Typography>
      ),
    },
    {
      field: "message",
      headerName: "Message",
      flex: 2,
      sortable: true,
      renderCell: (params) => (
        <Typography className={classes.messageText}>
          {params?.row?.message}
        </Typography>
      ),
    },
    {
      field: "passengers",
      headerName: "Passengers",
      flex: 1.2,
      sortable: true,
      renderCell: (params) => (
        <Typography className={classes.passengerText}>
          {params?.row?.passengers}
        </Typography>
      ),
    },
    {
      field: "createdat",
      headerName: "Created At",
      flex: 1.2,
      sortable: true,
      renderCell: (params) => (
        <Typography className={classes.dateText}>
          {params?.row?.createdat}
        </Typography>
      ),
    },
  ];

  const handleSendMessage = (selecteddatas, message) => {
    dispatch(showLoader("Sending SMS..."));
    const data = {
      selecteddatas: selecteddatas,
      message: message,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "SMS sent Successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        getnotifications();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while sending SMS",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    notificationApi.sendsmsnotifications({ ...data }).then(onSuccess, onFailure);
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const handleDeleteClick = () => {
    if (selectedRows.length === 0) {
      dispatch(
        showSnackbar({
          message: "No rows selected",
          autoHideDuration: 1000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "warning",
        })
      );
      return;
    }
    setOpenDialog(true);
  };

  const handleDeleteConfirm = () => {
    setOpenDialog(false);
    const idsToDelete = selectedRows;

    dispatch(showLoader("Deleting..."));

    const onSuccess = () => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: "Deleted successfully!",
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        })
      );
      getnotifications();
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Delete failed",
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        })
      );
    };

    notificationApi
      .deleteMultipleNotifications({ ids: idsToDelete })
      .then(onSuccess, onFailure);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Pagination calculation like ServiceType
  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Top Selector & Content Card */}
      <Box className={classes.bulknotifyblock}>
        <BulkSMSSelector
          passengers={dataRows}
          onSendMessage={handleSendMessage}
        />
      </Box>

      {/* Toolbar & Export Buttons Row */}
      <Box className={classes.toolbarRow}>
        <Box display="flex" alignItems="center" gap={1.5}>
          {selectedRows.length > 0 && (
            <Button
              className={classes.deleteBtn}
              variant="contained"
              startIcon={<Delete />}
              onClick={handleDeleteClick}
            >
              Delete Selected ({selectedRows.length})
            </Button>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          <Box className={classes.exportButtonGroup}>
            <Button onClick={handleCopy}>Copy</Button>
            <Button onClick={handleDownloadExcel}>Excel</Button>
            <Button onClick={handleDownloadCSV}>CSV</Button>
            <Button onClick={handleDownloadPDF}>PDF</Button>
          </Box>
          <Button
            onClick={handleDownloadAll}
            sx={{
              backgroundColor: "#111827",
              color: "#FFFFFF",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "none",
              px: 2,
              py: 0.8,
              "&:hover": { backgroundColor: "#1F2937" },
            }}
          >
            Download All
          </Button>
        </Box>
      </Box>

      {/* Data Table */}
      <Box className={classes.dataGridTable}>
        <DataGrid
          rows={notifydataRows}
          columns={columns}
          pageSize={pageSize}
          autoHeight
          checkboxSelection
          disableSelectionOnClick
          onSelectionModelChange={handleSelectionChange}
          selectionModel={selectedRows}
          rowHeight={64}
          hideFooter
        />

        {/* ServiceType Style Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (notifydataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {selectedRows.length} selected{" "}
            {selectedRows.length === 1 ? "row" : "rows"}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ color: "#fff" }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Passenger;
